/* ═══════════════════════════════════════════════════════
   SwipeHome — Trust Layer Module
   HomeCheck, MarketPulse, Claim page render functions
   ═══════════════════════════════════════════════════════ */

import { api } from './api.js';
import { navigate } from './router.js';
import type {
  HomecheckResponse,
} from './types/api.js';
import type { Listing } from '../../shared/types.js';


// Unused-import quieter for the scratch state properties referenced below.

// ═══════════════════════════════════════════════════════
// HomeCheck — Property Health Score
// ═══════════════════════════════════════════════════════

/** Legacy HomeCheck renderer expects a richer object than the typed
 * HomecheckResponse — the backend enriches the response with per-factor
 * detail fields (icon, label, summary, condition, detail, points) that
 * aren't in the shared shape. We keep a loose local interface. */
interface HomecheckFactorExtra {
  icon: string;
  label: string;
  summary: string;
  condition: string;
  detail: string;
  points: number;
  max_points: number;
  data_source?: string;
}

// The legacy HomeCheck renderer reads a richer shape than the shared
// HomecheckResponse type — we keep both in a plain union rather than
// extending, since the backend may send either.
interface HomecheckExtendedResponse extends Omit<HomecheckResponse, 'factors'> {
  score?: number;
  max_score?: number;
  grade?: string;
  overall_condition?: string;
  good_count?: number;
  attention_count?: number;
  factors?: HomecheckFactorExtra[] | null;
  data_sources?: string | string[];
}

export function isHomecheckDemo(): boolean {
  return true; // Trust Layer features available for all users
}

export function hcScoreColor(score: number): string {
  if (score >= 75) return 'var(--success)';
  if (score >= 50) return 'var(--warning)';
  return 'var(--danger)';
}

export function hcCondBadge(cond: string): string {
  const colors: Record<string, string> = {
    good: 'var(--success)',
    fair: 'var(--warning)',
    attention: 'var(--danger)',
  };
  const labels: Record<string, string> = {
    good: 'Good',
    fair: 'Fair',
    attention: 'Attention',
  };
  const c = colors[cond] || 'var(--text-secondary)';
  return `<span class="hc-cond-badge" style="color:${c};background:${c}15;border-color:${c}30">${labels[cond] || cond}</span>`;
}

export function renderHomecheckFull(data: HomecheckExtendedResponse): string {
  const score = data.score || 0;
  const maxScore = data.max_score || 100;
  const color = hcScoreColor(score);
  const pct = Math.round((score / maxScore) * 100);
  const circ = 251.3;
  const offset = circ - (circ * pct) / 100;
  const factors = data.factors || [];

  // All risk factors start collapsed — the score ring + good/attention
  // counts are the summary; "Show All Factors" reveals the list.
  const factorsHtml = factors
    .map(
      (f) => `
    <div class="hc-factor hc-factor-hidden" onclick="this.classList.toggle('expanded')">
      <div class="hc-factor-row">
        <div class="hc-factor-icon"><i data-feather="${f.icon}" width="16"></i></div>
        <div class="hc-factor-info">
          <div class="hc-factor-label">${f.label}</div>
          <div class="hc-factor-summary">${f.summary}</div>
        </div>
        ${hcCondBadge(f.condition)}
        <i data-feather="chevron-down" width="14" class="hc-factor-chevron"></i>
      </div>
      <div class="hc-factor-detail">
        <p>${f.detail}</p>
        ${f.data_source ? `<div style="font-size:10px;color:var(--text-secondary);margin-top:4px">Source: ${f.data_source}</div>` : ''}
        <div class="hc-factor-bar-wrap">
          <div class="hc-factor-bar-track">
            <div class="hc-factor-bar-fill" style="width:${Math.round((f.points / f.max_points) * 100)}%;background:${hcScoreColor(Math.round((f.points / f.max_points) * 100))}"></div>
          </div>
          <span class="hc-factor-pts">${f.points}/${f.max_points} pts</span>
        </div>
      </div>
    </div>
  `,
    )
    .join('');

  const dataSources = Array.isArray(data.data_sources)
    ? data.data_sources.join(', ')
    : `${data.data_sources || ''} data sources`;

  return `
    <div class="hc-score-row">
      <div class="hc-score-ring">
        <svg width="90" height="90" viewBox="0 0 90 90">
          <circle cx="45" cy="45" r="40" fill="none" stroke="var(--surface)" stroke-width="6"/>
          <circle cx="45" cy="45" r="40" fill="none" stroke="${color}" stroke-width="6"
            stroke-dasharray="${circ}" stroke-dashoffset="${offset}"
            stroke-linecap="round" transform="rotate(-90 45 45)"
            style="transition:stroke-dashoffset 1s ease"/>
        </svg>
        <div class="hc-score-number" style="color:${color}">${score}</div>
      </div>
      <div class="hc-score-summary">
        <div class="hc-score-label" style="color:${color}">${data.grade ? data.grade + ' \u2014 ' : ''}${data.overall_condition === 'good' ? 'Good Condition' : data.overall_condition === 'fair' ? 'Fair Condition' : 'Needs Attention'}</div>
        <div class="hc-score-badges">
          ${data.good_count && data.good_count > 0 ? `<span class="hc-count-badge hc-count-good">${data.good_count} good</span>` : ''}
          ${data.attention_count && data.attention_count > 0 ? `<span class="hc-count-badge hc-count-attention">${data.attention_count} attention</span>` : ''}
        </div>
      </div>
    </div>
    <div class="hc-factors">${factorsHtml}</div>
    ${factors.length > 0 ? `<button class="hc-show-all-btn" onclick="toggleHomecheckFactors(this)">${hcShowAllLabel(factors.length)}</button>` : ''}
    <div class="hc-footer">
      <span>${dataSources}</span>
      <a href="javascript:void(0)" onclick="showToast('HomeCheck&trade; combines FEMA flood zone data, EPA environmental hazard records, and property condition estimates to assess risk.', 'info')">How is this calculated?</a>
    </div>
  `;
}

export function renderHomecheckComingSoon(): string {
  return `
    <div class="hc-coming-soon">
      <div class="hc-cs-header">
        <div class="hc-cs-icon"><i data-feather="shield" width="28" style="color:var(--primary)"></i></div>
        <div>
          <div class="hc-cs-title">HomeCheck&trade;</div>
          <span class="hc-cs-badge">Coming Soon</span>
        </div>
      </div>
      <p class="hc-cs-desc">Property condition report \u2014 know the condition before you buy.</p>
      <div class="hc-cs-features">
        <div><i data-feather="file-text" width="13"></i> Permit history</div>
        <div><i data-feather="home" width="13"></i> Roof & HVAC age</div>
        <div><i data-feather="clock" width="13"></i> Ownership stability</div>
        <div><i data-feather="droplet" width="13"></i> Climate risk factors</div>
      </div>
      <button class="btn btn-secondary btn-block btn-sm" onclick="showToast('You\\'ll be notified when HomeCheck&trade; launches!', 'success')">
        <i data-feather="bell" width="14"></i> Get Early Access
      </button>
    </div>
  `;
}

function hcShowAllLabel(count: number): string {
  return `Show All Factors (${count}) <i data-feather="chevron-down" width="14"></i>`;
}

export function toggleHomecheckFactors(btn: HTMLElement): void {
  const hidden = document.querySelectorAll<HTMLElement>('.hc-factor-hidden');
  const showing = btn.dataset.showing === 'true';
  hidden.forEach((el) => (el.style.display = showing ? 'none' : 'block'));
  btn.dataset.showing = showing ? '' : 'true';
  btn.innerHTML = showing
    ? hcShowAllLabel(hidden.length)
    : 'Show Less <i data-feather="chevron-up" width="14"></i>';
  if (window.feather) window.feather.replace();
}

type ListingLike = Pick<Listing, 'id'> & { address_line1?: string; address?: string };

export async function loadHomeCheck(listing: ListingLike): Promise<void> {
  const container = document.getElementById('homecheck-content');
  if (!container) return;
  const address = listing.address_line1 || listing.address || '';
  if (!address) {
    container.innerHTML = renderHomecheckComingSoon();
    if (window.feather) window.feather.replace();
    return;
  }
  try {
    const data = await api<HomecheckExtendedResponse>(
      'GET',
      `/homecheck/properties/${listing.id}?address=${encodeURIComponent(address)}`,
    );
    if (data.available === false) {
      container.innerHTML = renderHomecheckComingSoon();
    } else {
      container.innerHTML = renderHomecheckFull(data);
    }
  } catch {
    container.innerHTML = renderHomecheckComingSoon();
  }
  if (window.feather) window.feather.replace();
}

export async function loadHomecheckSwipeBadge(listing: ListingLike): Promise<void> {
  const address = listing.address_line1 || listing.address || '';
  if (!address) return;
  try {
    const data = await api<HomecheckExtendedResponse>(
      'GET',
      `/homecheck/properties/${listing.id}?address=${encodeURIComponent(address)}`,
    );
    if (data.score != null) {
      const badge = document.getElementById(`hc-badge-${listing.id}`);
      if (badge) {
        const scoreEl = badge.querySelector<HTMLElement>('.hc-badge-score');
        if (scoreEl) scoreEl.textContent = String(data.score);
        badge.style.borderColor = hcScoreColor(data.score);
        badge.style.color = hcScoreColor(data.score);
      }
    }
  } catch {
    /* silent */
  }
}

export function loadHomecheckListBadges(): void {
  document.querySelectorAll<HTMLElement>('.hc-list-badge').forEach(async (el) => {
    const address = el.dataset.address;
    const id = el.id.replace('hc-list-', '');
    if (!address) return;
    try {
      const data = await api<HomecheckExtendedResponse>(
        'GET',
        `/homecheck/properties/${id}?address=${encodeURIComponent(address)}`,
      );
      if (data.score != null) {
        const color = hcScoreColor(data.score);
        el.innerHTML = `<div class="hc-list-pill" style="color:${color};border-color:${color}30"><i data-feather="shield" width="10"></i>${data.score}</div>`;
        if (window.feather) window.feather.replace();
      }
    } catch {
      /* silent */
    }
  });
}

// navigate is referenced from inline onclick handlers only.
void navigate;
